import { Link, useLocation } from "react-router-dom";
import {
  addDays,
  addWeeks,
  format,
  isSameDay,
  startOfWeek,
  subWeeks,
} from "date-fns";
import type { PublicEvent } from "@vedaunion/shared";
import { modalLinkState } from "../../lib/modal-routing";

/**
 * Seven-day grid keyed to the week containing `cursor`. Renders each event
 * as a positioned block whose top + height map to its start time + duration.
 * Multi-day events are rendered once per day (clipped to the day's bounds)
 * with a tiny chevron indicating continuation.
 *
 * The view assumes a 24-hour day with a 60-minute resolution; each hour is
 * 48px tall — enough for a one-line title at typical zoom. All-day events
 * are rendered in a slim strip above the timed grid.
 */

interface Props {
  cursor: Date;
  events: PublicEvent[];
  onCursorChange: (next: Date) => void;
  basePath?: string;
  /** Optional click-day-to-create handler; passes the wall-clock instant the
   *  user clicked. Used by /dashboard/calendar to open the create form
   *  pre-filled with that start time. */
  onCreateAt?: (when: Date) => void;
  /** When false, suppresses the internal "May 1 — May 7" + nav header so a
   *  parent can render its own (e.g. the dashboard's sub-tab row). */
  showHeader?: boolean;
}

const HOUR_HEIGHT = 32; // px per hour — 24h × 32 = 768px total, feels less endless
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function WeekView({ cursor, events, onCursorChange, basePath = "/calendar", onCreateAt, showHeader = true }: Props) {
  const location = useLocation();
  const weekStart = startOfWeek(cursor, { weekStartsOn: 1 });
  const days: Date[] = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  // Bucket events by day. All-day and multi-day events go in the all-day strip.
  const allDayBuckets = new Map<string, PublicEvent[]>();
  const timedBuckets = new Map<string, PublicEvent[]>();

  for (const ev of events) {
    const start = new Date(ev.startsAt);
    const end = new Date(ev.endsAt);
    const isMultiDay = !isSameDay(start, end);
    // An unannounced time has no slot in the hour grid — it sits with the day.
    if (ev.allDay || ev.timeTbd || isMultiDay) {
      let d = start;
      let safety = 0;
      while (d <= end && safety++ < 14) {
        if (d >= weekStart && d <= addDays(weekStart, 6)) {
          const k = format(d, "yyyy-MM-dd");
          const list = allDayBuckets.get(k) ?? [];
          list.push(ev);
          allDayBuckets.set(k, list);
        }
        d = addDays(d, 1);
      }
    } else {
      const k = format(start, "yyyy-MM-dd");
      const list = timedBuckets.get(k) ?? [];
      list.push(ev);
      timedBuckets.set(k, list);
    }
  }

  // Compute the time-based "now" line offset for today.
  const nowOffset = today.getHours() * HOUR_HEIGHT + (today.getMinutes() / 60) * HOUR_HEIGHT;

  const onColumnClick = (day: Date, e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateAt) return;
    if ((e.target as HTMLElement).closest("a")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutes = Math.round((y / HOUR_HEIGHT) * 60 / 15) * 15;
    const hour = Math.min(23, Math.max(0, Math.floor(minutes / 60)));
    const mm = minutes % 60;
    const at = new Date(day);
    at.setHours(hour, mm, 0, 0);
    onCreateAt(at);
  };

  return (
    <div className="week-view">
      {showHeader && (
        <header className="week-view-head">
          <h2 className="week-view-range">
            {format(weekStart, "d LLL")} — {format(addDays(weekStart, 6), "d LLL yyyy")}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="tt-btn"
              onClick={() => onCursorChange(subWeeks(cursor, 1))}
              title="Previous week"
              aria-label="Previous week"
            >
              ‹
            </button>
            <button
              type="button"
              className="tt-btn"
              onClick={() => onCursorChange(new Date())}
              title="This week"
            >
              Today
            </button>
            <button
              type="button"
              className="tt-btn"
              onClick={() => onCursorChange(addWeeks(cursor, 1))}
              title="Next week"
              aria-label="Next week"
            >
              ›
            </button>
          </div>
        </header>
      )}

      <div className="week-grid">
        {/* Column headers */}
        <div className="week-col-time" />
        {days.map((d, i) => (
          <div key={d.toISOString()} className={`week-col-head ${isSameDay(d, today) ? "is-today" : ""}`}>
            <span className="week-col-head-day">{DAY_LABELS[i]}</span>
            <span className="week-col-head-date">{format(d, "d")}</span>
          </div>
        ))}

        {/* All-day strip */}
        <div className="week-col-time week-allday-label">all-day</div>
        {days.map((d) => {
          const k = format(d, "yyyy-MM-dd");
          const items = allDayBuckets.get(k) ?? [];
          return (
            <div key={`ad-${k}`} className="week-allday-cell">
              {items.map((ev) => (
                <Link
                  key={`${ev.occurrenceId}-${k}`}
                  to={`${basePath}/${ev.slug}`}
                  state={modalLinkState(location)}
                  className={`cal-event ${ev.status === "cancelled" ? "is-cancelled" : ""}`}
                  data-cat={ev.category}
                  title={ev.title}
                >
                  {ev.title}
                </Link>
              ))}
            </div>
          );
        })}

        {/* Time gutter */}
        <div className="week-time-gutter">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="week-hour-label" style={{ height: HOUR_HEIGHT }}>
              {h === 0 ? "" : `${String(h).padStart(2, "0")}:00`}
            </div>
          ))}
        </div>

        {/* Day columns with timed events */}
        {days.map((d) => {
          const k = format(d, "yyyy-MM-dd");
          const items = timedBuckets.get(k) ?? [];
          const dayIsToday = isSameDay(d, today);
          return (
            <div
              key={`col-${k}`}
              className={`week-day-col ${dayIsToday ? "is-today" : ""}`}
              onClick={(e) => onColumnClick(d, e)}
              role={onCreateAt ? "button" : undefined}
            >
              {/* Hour grid lines */}
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="week-hour-row" style={{ height: HOUR_HEIGHT }} />
              ))}
              {/* Now marker */}
              {dayIsToday && (
                <div className="week-now" style={{ top: nowOffset }} aria-hidden />
              )}
              {/* Events */}
              {items.map((ev) => {
                const start = new Date(ev.startsAt);
                const end = new Date(ev.endsAt);
                const top = start.getHours() * HOUR_HEIGHT + (start.getMinutes() / 60) * HOUR_HEIGHT;
                const heightMin = Math.max(28, ((end.getTime() - start.getTime()) / 60000 / 60) * HOUR_HEIGHT);
                return (
                  <Link
                    key={ev.occurrenceId}
                    to={`${basePath}/${ev.slug}`}
                    state={modalLinkState(location)}
                    className={`week-event cal-event ${ev.status === "cancelled" ? "is-cancelled" : ""}`}
                    data-cat={ev.category}
                    style={{ top, height: heightMin }}
                    title={ev.title}
                  >
                    <span className="week-event-time">{format(start, "HH:mm")}</span>
                    <span className="week-event-title">{ev.title}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
